import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as yup from "yup";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import ClosIcon from "src/assets/svg-icons/close.svg";
import {
  useAddClientAccountUserMutation,
  useUpdateClientAccountUserMutation,
} from "src/api/clients";
import { LoadingButton } from "@mui/lab";
import { useParams } from "react-router-dom";
import { emailRegex } from "src/constants/regex";
import { ILocation } from "src/types/locations";
import { IUserAccount } from "src/types/clients";
import DeleteIcon from "src/assets/svg-icons/delete.svg";

const getLocationServiceKey = (service: { type: string; name: string }) =>
  JSON.stringify([service.type, service.name]);

export const dashPermissionsList = [
  {
    label: "Average stay time per day (Numbers)",
    value: "average_stay_number",
  },
  { label: "Average stay time per day (Charts)", value: "average_stay_charts" },
  { label: "Free Tickets (Numbers)", value: "free_tickets_number" },
  { label: "Free Tickets (Charts)", value: "free_tickets_charts" },
  { label: "Total Sales (Numbers)", value: "total_sales_number" },
  { label: "Total Sales (Charts)", value: "total_sales_charts" },
  { label: "Total Number of Cars (Numbers)", value: "total_cars_number" },
  { label: "Total Number of Cars (Charts)", value: "total_cars_charts" },
];

export const viewExportPermissionsList = [
  {
    label: "View",
    value: "view",
  },
  { label: "Export", value: "export" },
];

export const golfCartsPermissionsList = [
  ...viewExportPermissionsList,
  { label: "Give For Free", value: "giveForFree" },
];

export const schema = yup.object().shape({
  name: yup
    .string()
    .required("Required")
    .max(100, "Can not exceed 100 characters"),
  email: yup
    .string()
    .required("Required")
    .max(100, "Can not exceed 100 characters")
    .test("is-valid-email", "Invalid Email", (value) => {
      if (!value) return true;
      return emailRegex.test(value);
    }),
  dashPermissions: yup.array().of(yup.string().required("Required")),
  ticketsValidationsPermissions: yup
    .array()
    .of(yup.string().required("Required")),
  golfCartsPermissions: yup.array().of(yup.string().required("Required")),
  locationsServices: yup.array().of(
    yup.object().shape({
      locationId: yup.string().required("Required"),

      locationServicesOptions: yup
        .array()
        .of(
          yup.object({
            type: yup.string().required("Required"),
            name: yup.string().required("Required"),
          }),
        ),

      services: yup
        .array()
        .of(yup.string().required("Required"))
        .min(1, "At least one service is required"),
    }),
  ),
});

export interface IFormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  onClose: any;
  refetch: Function;
  locations: ILocation[];
  user?: IUserAccount;
}

const AddEditUserPopup: React.FC<IProps> = (props) => {
  const { open, onClose, refetch, locations, user } = props;
  const { id } = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !!user ? "edit" : "add";

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm<IFormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      dashPermissions: [],
      ticketsValidationsPermissions: [],
      golfCartsPermissions: [],
      locationsServices: [
        {
          locationId: "",
          services: [],
          locationServicesOptions: [],
        },
      ],
    },
  });

  const {
    fields: locationServicesFields,
    append: appendLocationService,
    remove: removeLocationService,
  } = useFieldArray({
    control,
    name: "locationsServices",
  });

  const handleAppendLocation = () => {
    appendLocationService({
      locationId: "",
      services: [],
    });
  };

  const handleRemoveLocation = (index: number) => {
    removeLocationService(index);
  };

  // APIs
  const { mutate, status, isPending, error } =
    useAddClientAccountUserMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateClientAccountUserMutation();

  const handleClose = () => {
    onClose();
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    const {
      name,
      email,
      dashPermissions,
      ticketsValidationsPermissions,
      golfCartsPermissions,
      locationsServices,
    } = data;
    if (
      dashPermissions?.length === 0 &&
      ticketsValidationsPermissions?.length === 0 &&
      golfCartsPermissions?.length === 0
    ) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Select Permissions",
        },
      ]);
      return;
    }
    const dataToSend = {
      clientId: id,
      name: name,
      email: email,
      permissions: {
        client_dashboard: {
          free_tickets_number: dashPermissions?.includes("free_tickets_number"),
          free_tickets_charts: dashPermissions?.includes("free_tickets_charts"),
          total_sales_number: dashPermissions?.includes("total_sales_number"),
          total_sales_charts: dashPermissions?.includes("total_sales_charts"),
          total_cars_number: dashPermissions?.includes("total_cars_number"),
          total_cars_charts: dashPermissions?.includes("total_cars_charts"),
          average_stay_number: dashPermissions?.includes("average_stay_number"),
          average_stay_charts: dashPermissions?.includes("average_stay_charts"),
        },
        ticket_validation: {
          view: ticketsValidationsPermissions?.includes("view"),
          export: ticketsValidationsPermissions?.includes("export"),
        },
        golf_carts_requests: {
          view: golfCartsPermissions?.includes("view"),
          export: golfCartsPermissions?.includes("export"),
          giveForFree: golfCartsPermissions?.includes("giveForFree"),
        },
      },
      clientAllowedLocationsId: locationsServices?.map((loc) => loc.locationId),
      clientAllowedLocationServices: locationsServices?.map((loc) => {
        const selectedServices = (loc.locationServicesOptions ?? []).filter(
          (service) => loc.services?.includes(getLocationServiceKey(service)),
        );
        return {
          locationId: loc.locationId,
          serviceTypes: selectedServices
            .map((service) => service.type)
            .filter((type, index, types) => types.indexOf(type) === index),
          services: selectedServices.map(({ type, name }) => ({ type, name })),
        };
      }),
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({ id: user?.id, ...dataToSend });
    }
  };

  useEffect(() => {
    if (!!user) {
      const clientDashPermissions = user.permissions.client_dashboard;
      const ticketPermissions = user.permissions.ticket_validation;
      const golfPermissions = user.permissions.golf_carts_requests;
      reset({
        name: user.name,
        email: user.email,
        locationsServices: (user.clientAllowedLocationServices ?? []).map(
          (loc: any) => {
            const selectedLocation = locations.find(
              (item: any) => item.id === loc.locationId,
            );

            const locServices =
              selectedLocation?.services?.map((service: any) => ({
                type: service.type,
                name: service.name,
              })) ?? [];
            const savedServices = loc.services ?? [];
            const locationServicesOptions = [
              ...locServices,
              ...savedServices.filter(
                (service: { type: string; name: string }) =>
                  !locServices.some(
                    (option: { type: string; name: string }) =>
                      getLocationServiceKey(option) ===
                      getLocationServiceKey(service),
                  ),
              ),
            ];

            return {
              locationId: loc.locationId,
              services: savedServices.map(getLocationServiceKey),
              locationServicesOptions,
            };
          },
        ),

        dashPermissions: clientDashPermissions
          ? (
              Object.entries(clientDashPermissions) as [
                keyof typeof clientDashPermissions,
                boolean,
              ][]
            )
              .filter(([_, value]) => value)
              .map(([key]) => key)
          : [],

        ticketsValidationsPermissions: ticketPermissions
          ? (
              Object.entries(ticketPermissions) as [
                keyof typeof ticketPermissions,
                boolean,
              ][]
            )
              .filter(([_, value]) => value)
              .map(([key]) => key)
          : [],

        golfCartsPermissions: golfPermissions
          ? (
              Object.entries(golfPermissions) as [
                keyof typeof golfPermissions,
                boolean,
              ][]
            )
              .filter(([_, value]) => value)
              .map(([key]) => key)
          : [],
      });
    }
  }, [user]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The user account has been added successfully!",
        },
      ]);
      handleClose();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.data?.errors?.[0]?.message || "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The user account has been updated successfully!",
        },
      ]);
      handleClose();
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data?.errors?.[0]?.message ||
            "Oops! Something went wrong.",
        },
      ]);
    }
  }, [statusUpdate]);

  useEffect(() => {
    if (!watch("ticketsValidationsPermissions")?.includes("view")) {
      setValue("ticketsValidationsPermissions", []);
    }
  }, [watch("ticketsValidationsPermissions")]);

  useEffect(() => {
    if (!watch("golfCartsPermissions")?.includes("view")) {
      setValue("golfCartsPermissions", []);
    }
  }, [watch("golfCartsPermissions")]);

  const locationsServices = watch("locationsServices");

  const selectedLocationWithServicesIds = locationsServices?.map(
    (loc) => loc.locationId,
  );

  const hasSelectedLocation = locationsServices?.some(
    (item) => !!item?.locationId,
  );

  return (
    <Dialog
      // fullWidth={true}
      maxWidth={"md"}
      open={open}
      onClose={handleClose}
      component={"div"}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          bgcolor: "#191919",
        },
      }}
    >
      <Box width={{ xs: "100%", md: "700px" }}>
        <DialogTitle
          sx={{ p: { xs: "15px 15px 10px 15px", md: "30px 30px 20px 30px" } }}
        >
          <Box display={"flex"} justifyContent={"flex-end"}>
            <Typography
              fontSize={{ xs: "17px", md: "20px" }}
              fontWeight={600}
              lineHeight={"22px"}
            >
              {mode === "add" ? "Add" : "Edit"} User
            </Typography>
            <IconButton size="small" sx={{ ml: "auto" }} onClick={handleClose}>
              <img src={ClosIcon} alt="" />
            </IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            px={{ xs: "15px", md: "30px" }}
            display={"flex"}
            flexDirection={"column"}
            gap={"22px"}
          >
            <Stack gap={"16px"} direction={{ xs: "column", md: "row" }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Name"
                    fullWidth
                    {...field}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    InputLabelProps={{
                      shrink: field.value !== undefined,
                    }}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Email"
                    fullWidth
                    size="small"
                    {...field}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    InputLabelProps={{
                      shrink: field.value !== undefined,
                    }}
                    disabled={mode === "edit"}
                  />
                )}
              />
            </Stack>
            <Box>
              <Typography mb={1} fontWeight={500}>
                User Permissions
              </Typography>
              <Stack gap={2} direction={{ xs: "column", md: "row" }}>
                <Box
                  p={1.5}
                  border={"1px solid "}
                  borderColor={
                    !!errors?.dashPermissions?.message ? "red" : "#5B5B5B"
                  }
                  flexBasis={{ xs: "100%", md: "50%" }}
                  borderRadius={"4px"}
                >
                  <Typography fontWeight={500}>Dashboard View</Typography>
                  <Controller
                    name={`dashPermissions`}
                    control={control}
                    render={({ field }) => {
                      const handleChange = (event: any) => {
                        const { value } = event.target;
                        const newValue = field.value?.includes(value)
                          ? field.value.filter((v) => v !== value) // remove if already selected
                          : [...(field.value || []), value]; // add if not selected
                        field.onChange(newValue);
                      };

                      const error = errors?.dashPermissions?.message;

                      return (
                        <FormControl
                          component="fieldset"
                          error={!!error}
                          variant="standard"
                        >
                          <FormGroup>
                            {dashPermissionsList.map((per) => (
                              <FormControlLabel
                                key={per.value}
                                control={
                                  <Checkbox
                                    value={per.value}
                                    checked={
                                      field.value?.includes(per.value) || false
                                    }
                                    onChange={handleChange}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography fontSize={"13px"}>
                                    {per.label}
                                  </Typography>
                                }
                              />
                            ))}
                          </FormGroup>
                          <FormHelperText id="my-helper-text">
                            {error}
                          </FormHelperText>
                        </FormControl>
                      );
                    }}
                  />
                </Box>
                <Stack flexBasis={{ xs: "100%", md: "50%" }} gap={2}>
                  {/* Tickets Validation */}
                  <Box
                    p={1.5}
                    border={"1px solid "}
                    borderColor={
                      !!errors?.dashPermissions?.message ? "red" : "#5B5B5B"
                    }
                    flexBasis={{ xs: "100%", md: "50%" }}
                    borderRadius={"4px"}
                  >
                    <Typography fontWeight={500}>Tickets Validation</Typography>
                    <Controller
                      name={`ticketsValidationsPermissions`}
                      control={control}
                      render={({ field }) => {
                        const handleChange = (event: any) => {
                          const { value } = event.target;
                          const newValue = field.value?.includes(value)
                            ? field.value.filter((v) => v !== value) // remove if already selected
                            : [...(field.value || []), value]; // add if not selected
                          field.onChange(newValue);
                        };

                        const error =
                          errors?.ticketsValidationsPermissions?.message;

                        return (
                          <FormControl
                            component="fieldset"
                            error={!!error}
                            variant="standard"
                          >
                            <FormGroup>
                              {viewExportPermissionsList.map((per) => (
                                <FormControlLabel
                                  key={per.value}
                                  control={
                                    <Checkbox
                                      value={per.value}
                                      checked={
                                        field.value?.includes(per.value) ||
                                        false
                                      }
                                      onChange={handleChange}
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography fontSize={"13px"}>
                                      {per.label}
                                    </Typography>
                                  }
                                  disabled={
                                    per.value === "export" &&
                                    !watch(
                                      "ticketsValidationsPermissions",
                                    )?.includes("view")
                                  }
                                />
                              ))}
                            </FormGroup>
                            <FormHelperText id="my-helper-text">
                              {error}
                            </FormHelperText>
                          </FormControl>
                        );
                      }}
                    />
                  </Box>
                  {/* golf carts */}
                  <Box
                    p={1.5}
                    border={"1px solid "}
                    borderColor={"#5B5B5B"}
                    flexBasis={{ xs: "100%", md: "50%" }}
                    borderRadius={"4px"}
                  >
                    <Typography fontWeight={500}>
                      Golf Carts Requests
                    </Typography>
                    <Controller
                      name={`golfCartsPermissions`}
                      control={control}
                      render={({ field }) => {
                        const handleChange = (event: any) => {
                          const { value } = event.target;
                          const newValue = field.value?.includes(value)
                            ? field.value.filter((v) => v !== value) // remove if already selected
                            : [...(field.value || []), value]; // add if not selected
                          field.onChange(newValue);
                        };

                        const error = errors?.golfCartsPermissions?.message;

                        return (
                          <FormControl
                            component="fieldset"
                            error={!!error}
                            variant="standard"
                          >
                            <FormGroup>
                              {golfCartsPermissionsList.map((per) => (
                                <FormControlLabel
                                  key={per.value}
                                  control={
                                    <Checkbox
                                      value={per.value}
                                      checked={
                                        field.value?.includes(per.value) ||
                                        false
                                      }
                                      onChange={handleChange}
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography fontSize={"13px"}>
                                      {per.label}
                                    </Typography>
                                  }
                                  disabled={
                                    ["export", "giveForFree"].includes(
                                      per.value,
                                    ) &&
                                    !watch("golfCartsPermissions")?.includes(
                                      "view",
                                    )
                                  }
                                />
                              ))}
                            </FormGroup>
                            <FormHelperText id="my-helper-text">
                              {error}
                            </FormHelperText>
                          </FormControl>
                        );
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>

            <Box>
              <Typography mb={2}>Assign Locations</Typography>
              <Stack gap={3}>
                {locationServicesFields.map((locServicesField, index) => {
                  const serviceOptions =
                    watch("locationsServices")?.[index]
                      ?.locationServicesOptions ?? [];

                  const selectedLocationId =
                    watch("locationsServices")?.[index]?.locationId;
                  return (
                    <Stack
                      key={locServicesField.id}
                      direction={"row"}
                      gap={2}
                      alignItems={"flex-start"}
                      width="100%"
                    >
                      <FormControl
                        size="small"
                        sx={{ flex: "5 1 0", minWidth: 0 }}
                      >
                        <Controller
                          name={`locationsServices.${index}.locationId`}
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              // loading={true}
                              size="small"
                              options={
                                (locations ?? []).filter(
                                  (item: any) =>
                                    !selectedLocationWithServicesIds?.includes(
                                      item.id,
                                    ) || item.id === field.value,
                                ) ?? []
                              }
                              value={
                                (locations ?? []).find(
                                  (option: any) => option.id === field.value,
                                ) ?? null
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              getOptionLabel={(option: any) => option?.nameEn}
                              getOptionKey={(option: any) => option.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={
                                    !!errors.locationsServices?.[index]
                                      ?.locationId
                                  }
                                  helperText={
                                    errors.locationsServices?.[index]
                                      ?.locationId?.message
                                  }
                                  label={"Location"}
                                />
                              )}
                              renderOption={(props, option: any) => (
                                <li {...props} key={option.id}>
                                  <Typography>{option.nameEn}</Typography>
                                </li>
                              )}
                              onChange={(_, data) => {
                                field.onChange(data?.id ?? null);

                                // to set services options
                                const services =
                                  data?.services
                                    ?.map((service: any) => ({
                                      type: service.type,
                                      name: service.name,
                                    })) ?? [];

                                setValue(
                                  `locationsServices.${index}.locationServicesOptions`,
                                  services,
                                );
                                // to reset services selected when location changes
                                setValue(
                                  `locationsServices.${index}.services`,
                                  [],
                                );
                              }}
                            />
                          )}
                        />
                      </FormControl>

                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="flex-start"
                        sx={{ flex: "7 1 0", minWidth: 0 }}
                      >
                        <FormControl
                          fullWidth
                          size="small"
                          error={
                            !!errors.locationsServices?.[index]?.services &&
                            !!selectedLocationId
                          }
                          sx={{ minWidth: 0, display: "block" }}
                          disabled={!selectedLocationId}
                        >
                          <InputLabel id="demo-multiple-checkbox-label">
                            Select Services
                          </InputLabel>
                          <Controller
                            name={`locationsServices.${index}.services`}
                            control={control}
                            defaultValue={[]}
                            render={({ field }) => (
                              <Select
                                {...field}
                                fullWidth
                                labelId="demo-multiple-checkbox-label"
                                id="demo-multiple-checkbox"
                                multiple
                                input={
                                  <OutlinedInput label={"Select Services"} />
                                }
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {selected.map((key) => {
                                      const service = serviceOptions.find(
                                        (option) =>
                                          getLocationServiceKey(option) === key,
                                      );
                                      return (
                                        <Chip
                                          size="small"
                                          key={key}
                                          label={service?.name ?? key}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              >
                                {serviceOptions.length > 0 ? (
                                  serviceOptions.map((service) => (
                                    <MenuItem
                                      key={getLocationServiceKey(service)}
                                      value={getLocationServiceKey(service)}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={field.value?.includes(
                                          getLocationServiceKey(service),
                                        )}
                                      />
                                      {service.name}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem disabled>No options</MenuItem>
                                )}
                              </Select>
                            )}
                          />
                          <FormHelperText id="my-helper-text">
                            {/* to show validation error only when location is selected*/}
                            {!!selectedLocationId
                              ? errors.locationsServices?.[index]?.services
                                  ?.message
                              : ""}
                          </FormHelperText>
                        </FormControl>
                        {locationServicesFields.length > 1 && (
                          <IconButton
                            onClick={() => {
                              handleRemoveLocation(index);
                            }}
                          >
                            <img src={DeleteIcon} alt="" />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}

                <Button
                  variant="outlined"
                  onClick={handleAppendLocation}
                  sx={{ width: "fit-content" }}
                >
                  {hasSelectedLocation
                    ? "Assign Another Location"
                    : "Assign Location"}
                </Button>
              </Stack>
            </Box>
          </Box>

          <DialogActions sx={{ padding: "31px", gap: "12px" }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              loading={isPending || isPendingUpdate}
              variant="contained"
              type="submit"
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </Box>
    </Dialog>
  );
};

export default AddEditUserPopup;
